import { useTranslation } from "react-i18next";
import styled from "styled-components";

import StaticPageLayout from "../../components/StaticPageLayout";
import { privacyPolicyContent } from "./content";

const PhoneIcon = () => (
    <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M7.49805 10.249L10.4247 12.0534C11.3905 12.6488 12.6094 12.6488 13.5751 12.0534L16.5018 10.249"
            stroke="#1677FF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <rect
            x="2.99609"
            y="5.49707"
            width="18.0075"
            height="14.0058"
            rx="3"
            stroke="#1677FF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const MailIcon = () => (
    <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M9.498 3.49609V5.29684C9.47304 5.59989 9.69563 5.867 9.99821 5.89709H13.9999C14.3025 5.867 14.5251 5.59989 14.5001 5.29684V3.49609"
            stroke="#1677FF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M9.99805 18.5022H13.9997"
            stroke="#1677FF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16.0003 21.5036H7.99693C6.8919 21.5036 5.99609 20.6078 5.99609 19.5028V5.49693C5.99609 4.3919 6.8919 3.49609 7.99693 3.49609H16.0003C17.1053 3.49609 18.0011 4.3919 18.0011 5.49693V19.5028C18.0011 20.6078 17.1053 21.5036 16.0003 21.5036Z"
            stroke="#1677FF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const PlatformRules = () => {
    const { i18n } = useTranslation();
    const locale = i18n.resolvedLanguage?.startsWith("en") ? "en" : "ua";
    const content = privacyPolicyContent[locale];

    return (
        <StaticPageLayout>
            <RulesWrapper>
                <h1>{content.title}</h1>
                <span className="policy-updates">{content.effectiveDate}</span>
                <span className="policy-subtitle">{content.subtitle}</span>
                {content.description.map((paragraph, index) => (
                    <p className="policy-description" key={`description-${index}`}>
                        {paragraph}
                    </p>
                ))}

                {content.sections.map(section => (
                    <div className="policy-section" key={section.title}>
                        <h3>{section.title}</h3>
                        {section.paragraphs.map((paragraph, index) => (
                            <p key={`${section.title}-${index}`}>{paragraph}</p>
                        ))}
                    </div>
                ))}

                <div className="policy-section contacts">
                    <h2>{content.contactsTitle}</h2>
                    <a href={`tel:${content.phoneLabel}`}>
                        <PhoneIcon />
                        {content.phoneLabel}
                    </a>
                    <a href={`mailto:${content.emailLabel}`}>
                        <MailIcon />
                        {content.emailLabel}
                    </a>
                </div>
            </RulesWrapper>
        </StaticPageLayout>
    );
};

export default PlatformRules;

const RulesWrapper = styled.div`
    padding: 35px 40px 45px;
    display: flex;
    max-width: 1020px;
    flex-direction: column;
    width: 100%;
    border-radius: 22px;
    background-color: #fff;
    border: 1px solid #e3ecff;
    box-shadow: 0 14px 45px rgba(16, 121, 255, 0.1);
    font-family: "Raleway";
    gap: 25px;
    margin: 0;
    position: relative;
    overflow: hidden;

    &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 6px;
        background: linear-gradient(90deg, #1677ff 0%, #5b8dff 50%, #9ad0ff 100%);
    }

    @media (max-width: 490px) {
        padding: 20px 16px 28px;
    }

    & > h1 {
        font-size: 50px;
        font-weight: 800;
        line-height: 65px;
        color: #212121;
        margin-top: 25px;

        @media (max-width: 821px) {
            padding: 0 20px;
            font-size: 40px;
            line-height: 52px;
        }

        @media (max-width: 490px) {
            padding: 0 20px;
            font-size: 36px;
            line-height: 46.8px;
        }
    }

    .policy-updates {
        font-weight: 600;
        font-size: 18px;
        font-family: "Roboto";
        line-height: 25.2px;
        color: #2d2d2d;

        @media (max-width: 821px) {
            padding: 0 20px;
            font-size: 16px;
            line-height: 22.4px;
        }
    }

    .policy-subtitle {
        font-weight: 400;
        font-size: 20px;
        line-height: 30px;
        font-family: "Roboto";
        color: #8c8c8c;

        @media (max-width: 821px) {
            padding: 0 20px;
            font-size: 18px;
            line-height: 27px;
        }
    }

    .policy-description {
        margin: 0;

        @media (max-width: 821px) {
            padding: 0 20px;
            font-size: 16px;
            line-height: 24px;
        }
    }

    p,
    a {
        font-weight: 400;
        font-size: 18px;
        line-height: 27px;
        color: #212121;
        font-family: "Roboto";
        margin: 0;

        @media (max-width: 821px) {
            font-size: 16px;
            line-height: 24px;
        }
    }

    .policy-section {
        display: flex;
        flex-direction: column;
        gap: 12px;

        h3 {
            font-size: 34px;
            font-weight: 700;
            line-height: 44.2px;
            font-family: "Raleway";
            color: #212121;
            margin: 0;

            @media (max-width: 821px) {
                font-size: 32px;
                line-height: 41.6px;
            }
        }

        h2 {
            font-weight: 700;
            font-size: 34px;
            line-height: 44.2px;
            font-family: "Raleway";
            margin: 0;

            @media (max-width: 821px) {
                font-size: 32px;
                line-height: 41.6px;
            }
        }

        @media (max-width: 821px) {
            padding: 0 20px;
        }
    }

    .contacts a {
        color: #1677ff;
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 6px;
        text-decoration: none;
    }
`;
